import { type ProfileDto } from "@crowdcoursing/api/openapi";
import { useQueryClient } from "@tanstack/react-query";
import { lazy, Suspense } from "react";
import { createBrowserRouter, createMemoryRouter, Navigate, Outlet, type RouteObject } from "react-router-dom";

import { AdminPageAsync } from "~ui/pages/AdminPage/AdminPageAsync";
import { AIChatPageAsync } from "~ui/pages/AIChatPage/AIChatPageAsync";
import { LegacyAppNavigationLayoutAsync } from "~ui/pages/AppNavigationLayout/AppNavigationLayoutAsync";
import { ApprenticeshipEnrollAsync } from "~ui/pages/ApprenticeshipEnroll/ApprenticeshipEnrollAsync";
import { CandidateSearchAsync } from "~ui/pages/CandidateSearch/CandidateSearchAsync";
import { SiteMasterCredentialsListPageAsync } from "~ui/pages/CandidateSearch/SiteMasterCredentialsListPageAsync";
import { CreateProfileAsync } from "~ui/pages/CreateProfile/CreateProfileAsync";
import { EmployerHiredSurveyPageRoute } from "~ui/pages/EmployerHiredSurvey/EmployerHiredSurveyPageRoute";
import { EventDashboardPage } from "~ui/pages/EventDashboardPage/EventDashboardPage";
import { HomeAsync } from "~ui/pages/Home/HomeAsync";
import { LawEnforcementCommandCenterPage } from "~ui/pages/LawEnforcementCommandCenter/LawEnforcementCommandCenterPage";
import { LoginAsync } from "~ui/pages/Login/LoginAsync";
import { LoginCallbackAsync } from "~ui/pages/LoginCallback/LoginCallbackAsync";
import { LoginVerificationAsync } from "~ui/pages/LoginVerification/LoginVerificationAsync";
import { Logout } from "~ui/pages/Logout/Logout";
import { MyEventsPageAsync } from "~ui/pages/MyEventsPage/MyEventsPageAsync";
import { MyProgramPageAsync } from "~ui/pages/MyProgram/MyProgramPageAsync";
import { MyRoomsPageAsync } from "~ui/pages/MyRoomsPage/MyRoomsPageAsync";
import { ArchivedAsync } from "~ui/pages/Notifications/ArchivedAsync";
import { InboxAsync } from "~ui/pages/Notifications/InboxAsync";
import { OrganizationAsync } from "~ui/pages/Organization/OrganizationAsync";
import { OrganizationOpportunityAsync } from "~ui/pages/Organization/OrganizationOpportunityAsync";
import { OrganizationProgramAsync } from "~ui/pages/Organization/OrganizationProgramAsync";
import { PlatformChatsPageAsync } from "~ui/pages/PlatformChatsPage/PlatformChatsPageAsync";
import { PublicDirectoryPageRoute } from "~ui/pages/PublicDirectoryPage/PublicDirectoryPageRoute";
import { PublicOrganizationPageRoute } from "~ui/pages/PublicOrganizationPage/PublicOrganizationPageRoute";
import { QuickStatusCheckPage } from "~ui/pages/QuickStatusCheck/QuickStatusCheckPage";
import { RecipientCommandCenterPage } from "~ui/pages/RecipientCommandCenter/RecipientCommandCenterPage";
import { ResourceLibraryPageAsync } from "~ui/pages/ResourceLibraryPage/ResourceLibraryPageAsync";
import { ResumeBuilderAsync } from "~ui/pages/ResumeBuilder/ResumeBuilderAsync";
import { RoomAttestationAsync } from "~ui/pages/RoomAttestation/RoomAttestationAsync";
import { RoomDashboardPage } from "~ui/pages/RoomDashboardPage/RoomDashboardPage";
import { RoomDay90Async } from "~ui/pages/RoomDay90/RoomDay90Async";
import { RoomSupervisorAsync } from "~ui/pages/RoomSupervisor/RoomSupervisorAsync";
import { RoomTimeExceptionsAsync } from "~ui/pages/RoomTimeExceptions/RoomTimeExceptionsAsync";
import { RoomWorksiteAdminsAsync } from "~ui/pages/RoomWorksiteAdmins/RoomWorksiteAdminsAsync";
import { DealDetailsAsync } from "~ui/pages/SalesPortal/DealDetailsAsync";
import { DealsAsync } from "~ui/pages/SalesPortal/DealsAsync";
import { PipelineAsync } from "~ui/pages/SalesPortal/PipelineAsync";
import { ProspectDetailsAsync } from "~ui/pages/SalesPortal/ProspectDetailsAsync";
import { ProspectsAsync } from "~ui/pages/SalesPortal/ProspectsAsync";
import { SalesContactsAsync } from "~ui/pages/SalesPortal/SalesContactsAsync";
import { SalesHomeAsync } from "~ui/pages/SalesPortal/SalesHomeAsync";
import { SalesTemplatesAsync } from "~ui/pages/SalesPortal/SalesTemplatesAsync";
import { ScheduledEmailsAsync } from "~ui/pages/SalesPortal/ScheduledEmailsAsync";
import { SettingsHubPageAsync } from "~ui/pages/SettingsHubPage/SettingsHubPageAsync";
import { DataAnalyticsPage } from "~ui/pages/SiteAdminPage/DataAnalyticsPage";
import { EntityManagementPage } from "~ui/pages/SiteAdminPage/EntityManagementPage";
import { TaskCalendarPage } from "~ui/pages/TaskCalendarPage/TaskCalendarPage";
import { TaskDashboardPage } from "~ui/pages/TaskDashboardPage/TaskDashboardPage";
import { ProfileRefresh } from "~ui/pages/UserProfile/ProfileRefresh";
import { UserProfileAsync } from "~ui/pages/UserProfile/UserProfileAsync";
import { EmergencyCommandCenterPage } from "~ui/pages/VisitorManagement/EmergencyCommandCenterPage";
import { VisitorManagementKioskPage } from "~ui/pages/VisitorManagement/VisitorManagementKioskPage";
import { VisitorManagementShell } from "~ui/pages/VisitorManagement/VisitorManagementShell";
import { VisitorsHomePage } from "~ui/pages/VisitorManagement/VisitorsHomePage";
import { isTokenExpired } from "~utils/url";

import { AppLayoutAsync } from "../../AppLayoutAsync";
import LoadingIndicatorFullPage from "../../cc-ui/components/LoadingIndicator/LoadingIndicatorFullPage";
import { CheckIn as EventCheckIn } from "../../components/Events/CheckIn";
import { Cancel as PaymentCancel, Success as PaymentSuccess } from "../../components/Payments";
import { RedirectToLogin } from "../../components/RedirectToLogin";
import { useSelector } from "../../hooks";
import { useIsTokenizedEmploymentSurveyRoute } from "../../hooks/useEmploymentSurveyAccess";
import { useFeature } from "../../hooks/useFeature";
import { CheckInPage } from "../../ui/pages/CheckIn/CheckInPage";
import { QRCodePage } from "../../ui/pages/CheckIn/QRCodePage";
import { RoomCheckInPage } from "../../ui/pages/CheckIn/RoomCheckInPage";
import { MyOrganizationsPage } from "../../ui/pages/MyOrganizationsPage/MyOrganizationsPage";
import { AdminOrganizationsPage } from "../../ui/pages/OrganizationsPage/AdminOrganizationsPage";
import { RawRoute, Route } from "./Route";

// Import the unified search page component using lazy loading
const AllSearchPage = lazy(() => import("../../ui/pages/AllSearchPage/AllSearchPage"));
const CandidateAttestationPage = lazy(() =>
  import("~ui/pages/CandidateAttestation/CandidateAttestationPage").then((module) => ({
    default: module.CandidateAttestationPage,
  })),
);

function useShouldRedirectToLogin() {
  const queryClient = useQueryClient();
  const isTokenizedEmploymentSurvey = useIsTokenizedEmploymentSurveyRoute();
  const whoAmIQuery = queryClient.getQueryCache().find<ProfileDto>({ queryKey: ["loginWhoAmI"] })?.state;
  const isProfileReady = whoAmIQuery?.data?.isProfileReady;
  const token = useSelector(({ auth: { token } }) => token);

  // Don't redirect if we're on the callback route
  if (window.location.pathname === "/login/google/callback") {
    return false;
  }

  return Boolean(
    !isTokenizedEmploymentSurvey &&
    (!token || isTokenExpired(token) || (whoAmIQuery?.status === "success" && !isProfileReady)),
  );
}

function WithRedirectToLogin({ children }: React.PropsWithChildren) {
  const shouldRedirectToLogin = useShouldRedirectToLogin();
  if (shouldRedirectToLogin) {
    return <RedirectToLogin />;
  }

  return <>{children}</>;
}

function VisitorManagementRouteGate({ children }: React.PropsWithChildren) {
  const allowed = useFeature("emergencyManagement");
  if (!allowed) {
    return <Navigate replace to={Route.home()} />;
  }
  return <>{children}</>;
}

function CandidateAttestationRouteGate({ children }: React.PropsWithChildren) {
  const allowed = useFeature("candidateAttestation");
  if (!allowed) {
    return <Navigate replace to={Route.home()} />;
  }
  return <>{children}</>;
}

export const routes: RouteObject[] = [
  // Base route; Redirect to login if not authenticated; Redirect to home if authenticated
  {
    element: (
      <WithRedirectToLogin>
        <Navigate to={Route.home()} />
      </WithRedirectToLogin>
    ),
    path: RawRoute.index,
  },

  // Routes not requiring authentication; No nav menu
  {
    children: [
      { element: <LoginAsync />, path: RawRoute.login },
      { element: <LoginCallbackAsync />, path: RawRoute.loginCallback },
      { element: <Logout />, path: RawRoute.logout },
      { element: <LoginVerificationAsync />, path: RawRoute.verify },
      { element: <CreateProfileAsync />, path: RawRoute.profileCreate },
      { element: <PublicDirectoryPageRoute />, path: RawRoute.publicDirectory },
      { element: <PublicOrganizationPageRoute />, path: RawRoute.publicOrg },
      { element: <LawEnforcementCommandCenterPage />, path: RawRoute.emergencyLawEnforcementCommandCenter },
      { element: <RecipientCommandCenterPage />, path: RawRoute.emergencyRecipientCommandCenter },
      { element: <QuickStatusCheckPage />, path: RawRoute.emergencyQuickStatusCheck },
    ],
    element: <LegacyAppNavigationLayoutAsync />,
  },

  // Routes requiring authentication; No nav menu
  {
    children: [
      {
        children: [
          { element: <QRCodePage />, path: RawRoute.podQr },
          { element: <QRCodePage />, path: RawRoute.eventQr },
          { element: <QRCodePage />, path: RawRoute.bubbleQr },
          { element: <RoomCheckInPage />, path: RawRoute.podCheckIn },
          { element: <ApprenticeshipEnrollAsync />, path: RawRoute.apprenticeshipEnroll },
          { element: <EventCheckIn />, path: RawRoute.eventCheckIn },
          { element: <CheckInPage />, path: RawRoute.bubbleCheckIn },
        ],
        element: <LegacyAppNavigationLayoutAsync />,
      },
    ],
    element: (
      <WithRedirectToLogin>
        <Outlet />
      </WithRedirectToLogin>
    ),
  },

  // Routes requiring authentication; Nav menu
  {
    children: [
      // with app navigation layout
      {
        children: [
          // home
          { element: <HomeAsync />, path: RawRoute.home },

          // admin
          { element: <AdminPageAsync />, path: RawRoute.admin },
          // Site admin - redirect base route to Platform Insights
          { element: <Navigate replace to={Route.adminSiteDataAnalytics()} />, path: RawRoute.adminSite },
          // Site admin pages
          { element: <DataAnalyticsPage />, path: RawRoute.adminSiteDataAnalytics },
          { element: <EntityManagementPage />, path: RawRoute.adminSiteEntityManagement },

          // organizations
          {
            element: <AdminOrganizationsPage />,
            path: RawRoute.organizations,
          },
          {
            element: <MyOrganizationsPage />,
            path: RawRoute.myOrganizations,
          },
          {
            element: <OrganizationAsync />,
            path: RawRoute.organization,
          },
          {
            element: <OrganizationOpportunityAsync />,
            path: RawRoute.organizationOpportunity,
          },
          {
            element: <OrganizationProgramAsync />,
            path: RawRoute.organizationProgram,
          },

          // notifications
          { element: <InboxAsync />, path: RawRoute.notifications },
          { element: <ArchivedAsync />, path: RawRoute.notificationsArchived },

          // payments — success/cancel kept for StripePay return URLs + as an example of the old room-payment flow
          { element: <ProfileRefresh />, path: RawRoute.profileRefresh },
          { element: <PaymentSuccess />, path: RawRoute.paymentSuccess },
          { element: <PaymentCancel />, path: RawRoute.paymentCancel },

          // profile
          { element: <UserProfileAsync />, path: RawRoute.profile },
          {
            element: (
              <CandidateAttestationRouteGate>
                <Suspense fallback={<LoadingIndicatorFullPage open />}>
                  <CandidateAttestationPage />
                </Suspense>
              </CandidateAttestationRouteGate>
            ),
            path: RawRoute.candidateAttestation,
          },

          {
            element: (
              <Suspense fallback={<LoadingIndicatorFullPage open />}>
                <SettingsHubPageAsync />
              </Suspense>
            ),
            path: RawRoute.settings,
          },

          // ai tutor
          { element: <AIChatPageAsync />, path: RawRoute.aiTutor },

          // tasks
          { element: <TaskDashboardPage />, path: RawRoute.taskDashboard },

          // rooms
          { element: <RoomDashboardPage />, path: RawRoute.roomView },
          { element: <RoomTimeExceptionsAsync />, path: RawRoute.roomTimeExceptions },
          { element: <RoomSupervisorAsync />, path: RawRoute.roomSupervisor },
          { element: <RoomDay90Async />, path: RawRoute.roomDay90 },
          { element: <RoomAttestationAsync />, path: RawRoute.roomAttestation },
          { element: <RoomWorksiteAdminsAsync />, path: RawRoute.roomWorksiteAdmins },
          {
            element: (
              <Suspense fallback={<LoadingIndicatorFullPage open />}>
                <MyRoomsPageAsync />
              </Suspense>
            ),
            path: RawRoute.myRooms,
          },
          {
            element: (
              <Suspense fallback={<LoadingIndicatorFullPage open />}>
                <Navigate replace to={`${Route.allSearch()}?type=rooms`} />
              </Suspense>
            ),
            path: RawRoute.roomSearch,
          },
          {
            element: (
              <Suspense fallback={<LoadingIndicatorFullPage open />}>
                <Navigate replace to={`${Route.allSearch()}?type=events`} />
              </Suspense>
            ),
            path: RawRoute.eventSearch,
          },
          {
            element: (
              <Suspense fallback={<LoadingIndicatorFullPage open />}>
                <AllSearchPage />
              </Suspense>
            ),
            path: RawRoute.allSearch,
          },

          // events
          { element: <EventDashboardPage />, path: RawRoute.eventView },
          {
            element: (
              <Suspense fallback={<LoadingIndicatorFullPage open />}>
                <PlatformChatsPageAsync />
              </Suspense>
            ),
            path: RawRoute.platformChats,
          },
          {
            element: (
              <Suspense fallback={<LoadingIndicatorFullPage open />}>
                <MyEventsPageAsync />
              </Suspense>
            ),
            path: RawRoute.myEvents,
          },
          {
            element: (
              <Suspense fallback={<LoadingIndicatorFullPage open />}>
                <MyProgramPageAsync />
              </Suspense>
            ),
            path: RawRoute.myProgram,
          },

          // resources
          {
            element: (
              <Suspense fallback={<LoadingIndicatorFullPage open />}>
                <ResourceLibraryPageAsync />
              </Suspense>
            ),
            path: RawRoute.resourceLibrary,
          },

          // resume
          { element: <ResumeBuilderAsync />, path: RawRoute.resumeBuilder },

          // employment verification
          {
            element: <EmployerHiredSurveyPageRoute />,
            path: RawRoute.orgCandidates,
          },

          // sales
          { element: <SalesHomeAsync />, path: RawRoute.salesPortalHome },
          { element: <SalesContactsAsync />, path: RawRoute.salesPortalContacts },
          { element: <DealDetailsAsync />, path: RawRoute.salesPortalDealDetails },
          { element: <DealsAsync />, path: RawRoute.salesPortalDeals },
          { element: <PipelineAsync />, path: RawRoute.salesPortalPipeline },
          { element: <ProspectsAsync />, path: RawRoute.salesPortalProspects },
          { element: <ProspectDetailsAsync />, path: RawRoute.salesPortalProspectDetails },
          { element: <SalesTemplatesAsync />, path: RawRoute.salesPortalTemplates },
          { element: <ScheduledEmailsAsync />, path: RawRoute.salesPortalScheduledEmails },
          { element: <TaskCalendarPage />, path: RawRoute.salesPortalTaskCalendar },

          // search
          { element: <CandidateSearchAsync />, path: RawRoute.searchCandidates },
          { element: <SiteMasterCredentialsListPageAsync />, path: RawRoute.searchCandidatesMasterCredentials },
        ],
        element: <AppLayoutAsync />,
      },
      {
        children: [
          { element: <VisitorsHomePage />, index: true },
          { element: <EmergencyCommandCenterPage />, path: "reunification/:dayOfEmergencyId" },
          { element: <EmergencyCommandCenterPage />, path: "reunification" },
          { element: <VisitorManagementKioskPage />, path: "kiosk" },
        ],
        element: (
          <VisitorManagementRouteGate>
            <VisitorManagementShell />
          </VisitorManagementRouteGate>
        ),
        path: RawRoute.schoolVisitorManagement,
      },
    ],
    element: (
      <WithRedirectToLogin>
        <Outlet />
      </WithRedirectToLogin>
    ),
  },
];

export const browserRouter = createBrowserRouter(routes, { future: { v7_relativeSplatPath: true } });

export const memoryRouter = createMemoryRouter(routes, { future: { v7_relativeSplatPath: true } });
